import Image from 'next/image';
import Link from 'next/link';
import type { ReactNode } from 'react';
import { AcademicCapIcon, BellAlertIcon, CalendarDaysIcon, ChatBubbleLeftRightIcon } from '@heroicons/react/24/outline';

const SIGNED_IN_FEATURES = [
  { icon: CalendarDaysIcon, title: 'Conflict-aware course schedules', detail: 'Saved schedules with campus travel time checked' },
  { icon: AcademicCapIcon, title: 'Degree requirements and planning', detail: 'Your majors and minors, semester by semester' },
  { icon: BellAlertIcon, title: 'Seat alerts', detail: 'A notice when a full section opens' },
  { icon: ChatBubbleLeftRightIcon, title: 'Answers about your own plan', detail: 'Ask Scarlet Sync with your courses in view' },
];

const PUBLIC_TOOLS = [
  { href: '/explore', label: 'Courses' },
  { href: '/explore?view=professors', label: 'Professors' },
  { href: '/explore?view=degrees', label: 'Degrees' },
  { href: '/bus-tracker', label: 'Buses' },
  { href: '/dining', label: 'Dining' },
];

export function GoogleMark() {
  return (
    <svg aria-hidden="true" className="h-5 w-5" viewBox="0 0 24 24">
      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
    </svg>
  );
}

export function LoginGatewayShell({
  title,
  description,
  children,
  footer,
  auditReady,
}: {
  title: string;
  description?: string;
  children: ReactNode;
  footer?: ReactNode;
  auditReady?: string;
}) {
  return (
    <div className="h-screen h-dvh overflow-y-auto bg-[var(--ss-canvas)] text-[var(--ss-text)]">
      <div className="mx-auto flex min-h-full w-full max-w-5xl flex-col px-4 py-4 sm:px-6 sm:py-6 lg:px-10">
        <Link className="inline-flex min-h-11 items-center gap-2.5 self-start rounded-control font-semibold" href="/">
          <Image alt="" height={28} priority src="/logo/scarlet-sync-no-bg.png" width={28} />
          Scarlet Sync
        </Link>

        <section
          aria-labelledby="login-gateway-title"
          className="my-auto grid gap-x-12 gap-y-6 py-6 sm:py-10 lg:grid-cols-[minmax(0,1fr)_25rem]"
          data-audit-ready={auditReady}
        >
          <header className="lg:col-start-1 lg:row-start-1 lg:self-end">
            <h1 id="login-gateway-title" className="text-3xl font-semibold leading-tight tracking-tight sm:text-4xl">
              Welcome to Scarlet Sync
            </h1>
            <p className="mt-3 max-w-lg text-base leading-7 text-[var(--ss-text-muted)]">
              Rutgers courses, schedules, and degree plans in one place.
            </p>
          </header>

          <div className="operational-surface !p-0 lg:col-start-2 lg:row-span-2 lg:row-start-1 lg:self-center">
            <div className="px-5 pb-4 pt-5 sm:px-6 sm:pt-6">
              <h2 className="text-xl font-semibold tracking-tight">{title}</h2>
              {description ? <p className="mt-2 text-sm leading-6 text-[var(--ss-text-muted)]">{description}</p> : null}
            </div>
            <div className="px-5 pb-5 sm:px-6 sm:pb-6">{children}</div>
            {footer ? <footer className="border-t border-[var(--ss-border)] px-5 py-1 text-sm sm:px-6">{footer}</footer> : null}
          </div>

          <div className="grid content-start gap-6 lg:col-start-1 lg:row-start-2">
            <div>
              <h3 className="text-sm font-semibold">Signing in adds</h3>
              <ul className="mt-2 divide-y divide-[var(--ss-border)] border-y border-[var(--ss-border)]">
                {SIGNED_IN_FEATURES.map(({ icon: Icon, title: featureTitle, detail }) => (
                  <li className="flex items-start gap-3 py-2.5" key={featureTitle}>
                    <Icon aria-hidden="true" className="mt-0.5 h-5 w-5 shrink-0 text-[var(--ss-brand)]" />
                    <p className="min-w-0 text-sm leading-5">
                      <span className="font-semibold">{featureTitle}</span>
                      <span className="block text-[var(--ss-text-muted)]">{detail}</span>
                    </p>
                  </li>
                ))}
              </ul>
            </div>
            <nav aria-label="Open without an account">
              <h3 className="text-sm font-semibold">Open without an account</h3>
              <ul className="mt-2 flex flex-wrap gap-2">
                {PUBLIC_TOOLS.map(({ href, label }) => (
                  <li key={href}>
                    <Link className="inline-flex min-h-9 items-center rounded-full border border-[var(--ss-border)] bg-[var(--ss-surface)] px-3 text-sm font-medium hover:bg-[var(--ss-surface-subtle)]" href={href}>{label}</Link>
                  </li>
                ))}
              </ul>
            </nav>
          </div>
        </section>
      </div>
    </div>
  );
}

export function AuthTransactionShell({
  eyebrow,
  title,
  description,
  children,
  footer,
  width = 'standard',
  auditReady,
}: {
  eyebrow: string;
  title: string;
  description: string;
  children: ReactNode;
  footer?: ReactNode;
  width?: 'standard' | 'wide';
  auditReady?: string;
}) {
  return (
    <div className="min-h-screen bg-[var(--ss-canvas)] px-4 py-6 text-[var(--ss-text)] sm:px-6 sm:py-10">
      <div className={`mx-auto flex min-h-[calc(100vh-3rem)] w-full items-center ${width === 'wide' ? 'max-w-4xl' : 'max-w-lg'} sm:min-h-[calc(100vh-5rem)]`}>
        <section
          aria-labelledby="auth-transaction-title"
          className="w-full overflow-hidden rounded-feature border border-[var(--ss-border)] bg-[var(--ss-surface)] shadow-e2"
          data-audit-ready={auditReady}
        >
          <header className="border-b border-[var(--ss-border)] bg-[var(--ss-surface-subtle)] px-5 py-5 sm:px-8 sm:py-6">
            <div className="flex items-start gap-4">
              <span className="grid h-12 w-12 shrink-0 place-items-center rounded-control border border-[var(--ss-border)] bg-[var(--ss-surface)]">
                <Image alt="" height={36} priority src="/logo/scarlet-sync-no-bg.png" width={36} />
              </span>
              <div className="min-w-0">
                <p className="operational-eyebrow">{eyebrow}</p>
                <h1 id="auth-transaction-title" className="mt-1 text-2xl font-semibold tracking-tight sm:text-3xl">{title}</h1>
                <p className="mt-2 max-w-2xl text-sm leading-6 text-[var(--ss-text-muted)]">{description}</p>
              </div>
            </div>
          </header>
          <div className="px-5 py-6 sm:px-8 sm:py-8">{children}</div>
          {footer ? <footer className="border-t border-[var(--ss-border)] px-5 py-4 text-sm text-[var(--ss-text-muted)] sm:px-8">{footer}</footer> : null}
        </section>
      </div>
    </div>
  );
}
